import React, { FC, useState, useEffect, useMemo } from 'react';
import { TrackingProp } from 'react-tracking';
import InputMask from 'react-input-mask';
import BEMHelper from '@ynap/bem-helper';
import { Tooltip } from '@ynap/notification';
import { CombinedSelect } from '@ynap/select';
import { ValidatedField } from '@ynap/field';
import { Field as FormField, FieldRenderProps } from '@ynap/form';
import { Messages, Field, AddressFinderParams, CheckoutAnalytics } from './@types';
import { snakeToCamel, getMessageByKey, validate } from './utils';
import AddressFinder from './AddressFinder';

type FieldFormat = {
    mask: string;
    placeholder: string;
};

export function getMaskFromField(field: Field, country: string): FieldFormat | null {
    if (country === 'JP' && field.format?.length === 1) {
        const format = field.format[0];
        return {
            mask: format.replace(/N/g, '9').replace(/A/g, 'a'),
            placeholder: format.replace(/N/g, '0').replace(/A/g, 'a'),
        };
    }
    return null;
}

export interface AddressFieldProps {
    innerKey: string;
    className: string;
    country: string;
    field: Field;
    initialValue?: string;
    messages?: Messages;
    errorStyle?: string;
    tracking?: TrackingProp;
    shouldValidateNonLatin: boolean;
    addressFinderParams: AddressFinderParams | null;
    foundValue?: string;
    isCheckoutLayout?: boolean;
    primaryCategory?: string;
    checkoutAnalytics?: CheckoutAnalytics;
    reduxForm: any;
    invalidFields?: string[];
}

type DropdownTouchedState = boolean | undefined;

export const AddressField: FC<AddressFieldProps> = ({
    innerKey,
    className,
    country,
    field,
    initialValue,
    messages,
    errorStyle = 'error',
    tracking,
    shouldValidateNonLatin,
    addressFinderParams,
    foundValue,
    isCheckoutLayout,
    primaryCategory,
    checkoutAnalytics,
    reduxForm = {},
    invalidFields
}) => {
    const { type, displayType, wcsType, values } = field;
    const { syncErrors = {} } = reduxForm;
    const name = wcsType?.name || '';

    let displayName = snakeToCamel(displayType || type);
    displayName = getMessageByKey(messages?.addressForm?.display, displayName) || displayName;
    const selectLabel = (name !== 'personTitle' && messages?.addressForm?.select?.({ select: displayName })) || ' ';
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [dropdownTouched, setDropdownTouched] = useState<DropdownTouchedState>();
    const [externalErrors, setExternalErrors] = useState<string[]>([]);
    const helpMessage = getMessageByKey(messages?.addressForm?.help, displayName);
    const fieldFormat = useMemo(() => {
        return getMaskFromField(field, country);
    }, [field, country]);

    useEffect(() => {
        if (invalidFields) {
            setExternalErrors(invalidFields);
        }
    }, [invalidFields]);

    const options = Array.isArray(values)
        ? values.map((option) => {
              if (typeof option == 'string') {
                  return { key: option, value: option, label: option };
              }
              const { display, value } = option;
              return { key: value, value, label: display };
          })
        : [];

    const tooltipMessage = getMessageByKey(messages?.addressForm?.tooltips, name?.toLowerCase());

    const showTooltip = () => {
        setTooltipOpen(true);

        if (String(name).toLowerCase() === 'pccc' && tracking) {
            tracking.trackEvent({
                event: 'view tooltip - PCCC',
                effect: 'view more information',
                category: {
                    primaryCategory: 'checkout',
                    subCategory: 'tooltip information',
                },
            });
        }
    };

    const handleClickOutTooltip = () => {
        setTooltipOpen(false);
    };

    useEffect(() => {
        // add when mounted
        document.addEventListener('mousedown', handleClickOutTooltip);
        // return function to be called when unmounted
        return () => {
            document.removeEventListener('mousedown', handleClickOutTooltip);
        };
    }, []);

    const getDefaultLabel = () => {
        if (!field.mandatory && (!['personTitle', 'addressFinder'].includes(name) || !isCheckoutLayout)) {
            const optionalMessage = messages?.addressForm?.optional?.() || '';
            return `${displayName} ${optionalMessage}`;
        }

        return displayName;
    };

    const getCustomLabel = () => {
        const tooltipBEM = new BEMHelper('AddressFieldTooltip');

        return {
            LabelWrap: Tooltip,
            labelWrapProps: {
                type: 'info',
                message: tooltipMessage,
                defaultOpen: false,
                open: tooltipOpen,
                getArrowDimensions: () => ({ left: 0, width: 'auto' }),
            },
            labelName: displayName,
            extraElements: <span className={tooltipBEM('info-icon')} onClick={showTooltip}></span>,
        };
    };

    // To stop error being triggered by Klarna we force the + sign in the phone number. This can be adjusted by the user if incorrect
    const addPrefixToPhoneNumber = (value: string, name: string): string => {
        const canAddPrefixToNumber = syncErrors?.billingAddress?.isKlarna && name === 'phone1' && value.length > 2 && value.split('')[0] !== '+';

        return canAddPrefixToNumber ? `+${value}` : value;
    };

    const removeUsedExternalError = (field: any) => {
        setExternalErrors((prev) => prev.filter((error) => error !== field))
    }

    return (
        <FormField
            key={innerKey}
            name={name}
            value={initialValue || ''}
            validate={validate(messages, field, displayName, country, shouldValidateNonLatin, syncErrors?.billingAddress?.isKlarna, externalErrors)}
            removable={true}
            foundValue={foundValue}
            readOnlyValue={field.value}
        >
            {({ value, onChange, onBlur, errors, touched, hint, showErrorNotification, ...rest }: FieldRenderProps) => {
                const showError = isCheckoutLayout && values ? dropdownTouched !== false : true;
                const error = touched && showError && errors?.[0] ? errors[0] : undefined;
                const enableSearch = name === 'state' || name === 'city';
                const validatedFieldProps = {
                    ...(tooltipMessage ? { customLabel: getCustomLabel() } : { label: getDefaultLabel() }),
                    error,
                    errorStyle: values ? 'highlight' : errorStyle,
                    help: hint || helpMessage,
                    htmlFor: name,
                    className,
                    showErrorNotification,
                };

                return (
                    <ValidatedField {...validatedFieldProps}>
                        <div>
                            {values ? (
                                <CombinedSelect
                                    value={value}
                                    onChange={(value: string) => {
                                        onChange(value);
                                        onBlur(value);
                                        removeUsedExternalError(name);
                                    }}
                                    onDropdown={() => {
                                        onBlur(value);
                                        setDropdownTouched((status) => (status === undefined ? false : status));
                                    }}
                                    onClose={() => isCheckoutLayout && setDropdownTouched(true)}
                                    emptyLabel={selectLabel}
                                    options={options}
                                    key={name}
                                    id={name}
                                    hasError={!!error}
                                    enableSearch={enableSearch}
                                    searchPlaceholder={messages?.addressForm?.search?.()}
                                />
                            ) : fieldFormat ? (
                                <InputMask
                                    id={name}
                                    type="text"
                                    value={value}
                                    mask={fieldFormat.mask}
                                    placeholder={fieldFormat.placeholder}
                                    maskChar={null}
                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {onChange(ev.target.value); removeUsedExternalError(name)}}
                                    onBlur={(ev: React.FocusEvent<HTMLInputElement>) => onBlur(ev.target.value)}
                                    {...rest}
                                />
                            ) : displayType === 'ADDRESS_FINDER' && addressFinderParams ? (
                                <AddressFinder
                                    addressFinderParams={addressFinderParams}
                                    country={country}
                                    tracking={tracking}
                                    primaryCategory={primaryCategory}
                                    checkoutAnalytics={checkoutAnalytics}
                                />
                            ) : (
                                <input
                                    id={name}
                                    type="text"
                                    value={value}
                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {onChange(addPrefixToPhoneNumber(ev.target.value, name)); removeUsedExternalError(name)}}
                                    onBlur={(ev: React.FocusEvent<HTMLInputElement>) => onBlur(ev.target.value)}
                                    {...rest}
                                />
                            )}
                        </div>
                    </ValidatedField>
                );
            }}
        </FormField>
    );
};
